import React, { useEffect, useState, useRef } from "react";
import { connect, useSelector } from "react-redux";
import Table from "../../../Components/TableLite/TableLite";
import Button from "Components/Button";
import { differenceInDays, format } from "date-fns";

// dependencies
import PropTypes from "prop-types";
import { BranchSelect } from "../../../Components/BranchSelector";
import {
	calculateServerOverallStateIcon,
	formatEnvironmentStatusDataForTableLiteRows,
	sendNotificationIfServerFlip,
} from "./actions";
import { cdkDeploy, cdkDeploymentLock } from "../../../Api/services/deployments";
import { getUserRoles, getUserIsAdmin, getCurrentEnvironmentName } from "Selectors/app";
import { useCdkDeploymentStatus, useCdkDeploymentLogs } from "../../../Api/services/deployments";
import { EnvironmentStatusOsxTerminal } from "./EnvironmentStatusOsxTerminal";
import { AlertCircle } from "lucide-react";

// css
import css from "./EnvironmentStatus.styl";

// hooks
import { useServerMonitor } from "./hooks";

const columns = [
	{
		id: "name",
		label: "Name",
		isSortable: false,
		width: "25%",
		getter: (row) => (
			<a href={row.healthUrl} target="_blank" rel="noreferrer">
				{row.name}
			</a>
		),
	},
	{
		id: "gitBranch",
		label: "Git Branch",
		isSortable: false,
		width: "25%",
		getter: (row) => (
			<div>
				{row.gitBranch === "ERROR" ? (
					<div className={css.error}>ERROR</div>
				) : (
					<div className={css.gitBranch}>
						<a
							href={`https://github.com/PerformYard/PerformYard/tree/${row.gitBranch}`}
							target="_blank"
							rel="noreferrer"
						>
							{row.gitBranch}
						</a>
					</div>
				)}
			</div>
		),
	},
	{
		id: "gitHashShort",
		label: "Git Hash",
		isSortable: false,
		width: "25%",
		getter: (row) => (
			<div>
				{row.gitHash === "ERROR" ? (
					<div className={css.error}>ERROR</div>
				) : (
					<a
						href={`https://github.com/PerformYard/PerformYard/commit/${row.gitHash}`}
						target="_blank"
						rel="noreferrer"
					>
						{row.gitHashShort}
					</a>
				)}
			</div>
		),
	},
	{
		id: "healthy",
		label: "Healthy",
		isSortable: false,
		width: "25%",
		getter: (row) => (
			<div>
				{row.healthy ? <div className={css.success}>Healthy</div> : <div className={css.error}>Unhealthy</div>}
			</div>
		),
	},
];

export const EnvironmentStatusTable = (props) => {
	const { environment, userRoles } = props;
	const [isDeploying, setDeploying] = React.useState(false);
	const [showLogs, setShowLogs] = React.useState(false);
	const [isUpdatingLock, setUpdatingLock] = React.useState(false);

	const webServer = environment?.webServer ? useServerMonitor(environment.webServer) : null;
	const apiServer = environment?.apiServer ? useServerMonitor(environment.apiServer) : null;
	const reportingServer = environment?.reportingServer ? useServerMonitor(environment.reportingServer) : null;
	const internalApi = environment?.internalApi ? useServerMonitor(environment.internalApi) : null;

	const [selectedBranch, setSelectedBranch] = useState(null);

	const loganEnv = useSelector(getCurrentEnvironmentName);

	// Check for GH branch data & user has permissions
	const showStatus =
		(userRoles.includes("engineering") && !["production", "sandbox", "demo"].includes(environment.lowerName)) ||
		userRoles.includes("logan-admin");

	const handleBranchSelect = (selectedOption) => {
		setSelectedBranch(selectedOption);
	};

	const handleDeployBranch = async () => {
		setDeploying(true);
		await cdkDeploy(loganEnv, {
			gitHash: selectedBranch.commitHash,
			gitBranch: selectedBranch.branchName,
			deploymentEnv: environment.lowerName,
		});
		setSelectedBranch(null);
		setDeploying(false);
	};

	// Handler for lock button to update lock status.
	const handleLockDeployment = async (newLockStatus) => {
		if (!deploymentId) {
			console.error("No deployment info to update");
			return;
		}
		setUpdatingLock(true);
		const resp = await cdkDeploymentLock(environment.lowerName, deploymentId, { lockStatus: newLockStatus });
		setUpdatingLock(false);
		if (!resp.ok) {
			console.error(resp.errors);
			alert(
				resp.errors + ["\nCongratulations! You lost the race condition.\nPlease pick a different deployment."]
			);
			return;
		}
		refetchDeployStatus();
	};

	const lockIsStale = (lockedAt) => {
		return differenceInDays(new Date(), new Date(lockedAt)) > 7;
	};

	useEffect(() => {
		if (webServer?.hashHistory) {
			sendNotificationIfServerFlip(webServer.hashHistory, `${environment.name}'s Web Server`);
		}
	}, [webServer.hashHistory]);

	// conditionally call the useCdkDeployment tanstack hook
	// if showStatus is true, start fetching deployment status
	const { data: deployStatus, refetch: refetchDeployStatus } = useCdkDeploymentStatus(
		environment.lowerName,
		showStatus,
		5000
	);
	const deploymentInfo = deployStatus?.data?.deploymentInfo;
	const deploymentId = deploymentInfo?._id;
	const deploymentLocked = deploymentInfo?.lock?.locked;

	const streamName = deploymentInfo?.actions.find((action) => action.action === "add-stream-name")?.info.streamName;
	// if showLogs is true, start fetching deployment logs/events
	const { data: deployLogs } = useCdkDeploymentLogs(
		environment.lowerName,
		streamName,
		showLogs && streamName != null,
		5000
	);
	const deploymentEvents = deployLogs?.data?.events || [];

	return (
		<div className={css.serverTable}>
			<div className={css.selectionWrapper}>
				<h1 className={css.tableTitle}>
					{environment.name} {calculateServerOverallStateIcon(webServer, apiServer, reportingServer)}
				</h1>

				{showStatus ? (
					<>
						<BranchSelect value={selectedBranch} onChange={handleBranchSelect} />

						<Button
							isRequesting={isDeploying}
							disabled={selectedBranch == null || deploymentLocked}
							onClick={handleDeployBranch}
						>
							Deploy
						</Button>

						<Button
							color={deploymentLocked ? "black" : "gray"}
							isRequesting={isUpdatingLock}
							onClick={() => handleLockDeployment(!deploymentLocked)}
						>
							{deploymentLocked ? "Unlock" : "Lock"}
						</Button>

						<Button color={showLogs ? "black" : "grey"} onClick={() => setShowLogs(!showLogs)}>
							Logs
						</Button>

						<Button color="green" href={environment.host} external={true} rel="noreferrer">
							Go
						</Button>
					</>
				) : null}
			</div>

			<div className={css.statusSection}>
				{showStatus ? (
					<div className={css.statusContainer}>
						<div className={css.deploymentInfo}>
							<div>
								<div className={css.label}>Started At</div>
								<div className={css.value}>
									{deploymentInfo?.createdAt
										? format(new Date(deploymentInfo?.createdAt), "MMM d h:mm a")
										: ""}
								</div>
							</div>

							<div>
								<div className={css.label}>Hash</div>
								<div className={css.value}>{deploymentInfo?.gitHash.substring(0, 7)}</div>
							</div>

							<div>
								<div className={css.label}>Deployed By</div>
								<div className={css.value}>{deploymentInfo?.user?.displayName ?? ""}</div>
							</div>

							<div>
								<div className={css.label}>Last Message</div>
								<div className={css.value}>
									{deploymentEvents.length > 0
										? format(
												new Date(deploymentEvents[deploymentEvents.length - 1].timestamp),
												"MMM d h:mm a"
											)
										: ""}
								</div>
							</div>

							<div>
								<div className={css.label}>Locked By</div>
								<div className={css.value}>
									{deploymentLocked ? (deploymentInfo?.lock?.lockedBy ?? "Unknown") : ""}
								</div>
							</div>

							<div>
								<div className={css.label}>Locked At</div>
								<div className={css.value}>
									{deploymentLocked
										? format(new Date(deploymentInfo?.lock?.lockedAt ?? null), "MMM d h:mm a")
										: ""}
									{deploymentLocked && lockIsStale(deploymentInfo?.lock?.lockedAt) ? (
										<AlertCircle className={css.warning} size={16} />
									) : (
										""
									)}
								</div>
							</div>
						</div>
						{showLogs ? (
							<EnvironmentStatusOsxTerminal
								deploymentEvents={deploymentEvents}
								closeOsxTerminal={() => setShowLogs(false)}
							/>
						) : null}
					</div>
				) : null}
			</div>

			<div className={css.tableContainer}>
				<Table
					isLoading={false}
					columns={columns}
					rows={
						false
							? []
							: formatEnvironmentStatusDataForTableLiteRows(
									webServer,
									apiServer,
									reportingServer,
									internalApi
								)
					}
				/>
			</div>
		</div>
	);
};

EnvironmentStatusTable.propTypes = {
	environment: PropTypes.object,
	userRoles: PropTypes.array.isRequired,
};

EnvironmentStatusTable.defaultProps = {
	environment: {},
};

const mapStateToProps = (state) => ({
	userIsAdmin: getUserIsAdmin(state),
	userRoles: getUserRoles(state),
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EnvironmentStatusTable);
